/**
 * Company data as TanStack queries under `["companies"]`, so a save, a link
 * or an import refreshes the list, the company page and every case panel.
 */
import { useQuery } from "@tanstack/react-query";

import { queryClient } from "@/lib/query/queryClient";

import {
  BillingAPI,
  CompaniesAPI,
  CompanyMigrationAPI,
  PlaybookAPI,
  PortalFirmAPI
} from "./api/companiesApi";

export const COMPANIES_KEY = ["companies"] as const;

export const companyKeys = {
  list: () => [...COMPANIES_KEY, "list"] as const,
  detail: (id: string) => [...COMPANIES_KEY, "detail", id] as const,
  cases: (id: string) => [...COMPANIES_KEY, "detail", id, "cases"] as const,
  forCase: (individualId: string) =>
    [...COMPANIES_KEY, "case", individualId] as const,
  matches: (name: string) => [...COMPANIES_KEY, "matches", name] as const,
  preview: (id: string, individualId: string) =>
    [...COMPANIES_KEY, "detail", id, "playbook", individualId] as const,
  billing: (id: string) => [...COMPANIES_KEY, "detail", id, "billing"] as const,
  nextExport: (id: string) =>
    [...COMPANIES_KEY, "detail", id, "billing", "next-export"] as const,
  migration: () => [...COMPANIES_KEY, "migration"] as const,
  logins: (id: string) => [...COMPANIES_KEY, "detail", id, "logins"] as const,
  requests: (id: string) =>
    [...COMPANIES_KEY, "detail", id, "requests"] as const,
  approvals: (id: string, individualId: string) =>
    [...COMPANIES_KEY, "detail", id, "approvals", individualId] as const,
  messages: (id: string, individualId: string) =>
    [...COMPANIES_KEY, "detail", id, "messages", individualId] as const
};

export const refreshCompanies = (): Promise<void> =>
  queryClient.invalidateQueries({ queryKey: COMPANIES_KEY });

/** Forward a `data-invalidate` list: the agent suggested a playbook rule. */
export const notifyCompaniesInvalidated = (
  targets: readonly string[]
): void => {
  if (targets.includes("playbook") || targets.includes("company_portal"))
    void refreshCompanies();
};

export const useCompanies = () =>
  useQuery(
    { queryKey: companyKeys.list(), queryFn: CompaniesAPI.list },
    queryClient
  );

export const useCompany = (id: string | undefined) =>
  useQuery(
    {
      queryKey: companyKeys.detail(id ?? ""),
      queryFn: () => CompaniesAPI.get(id ?? ""),
      enabled: Boolean(id)
    },
    queryClient
  );

export const useCompanyCases = (id: string | undefined) =>
  useQuery(
    {
      queryKey: companyKeys.cases(id ?? ""),
      queryFn: () => CompaniesAPI.cases(id ?? ""),
      enabled: Boolean(id)
    },
    queryClient
  );

export const useCaseCompany = (individualId: string | undefined) =>
  useQuery(
    {
      queryKey: companyKeys.forCase(individualId ?? ""),
      queryFn: () => CompaniesAPI.forCase(individualId ?? ""),
      enabled: Boolean(individualId),
      staleTime: 30_000
    },
    queryClient
  );

export const useCaseMatches = (name: string) =>
  useQuery(
    {
      queryKey: companyKeys.matches(name.trim().toLowerCase()),
      queryFn: () => CompaniesAPI.caseMatches(name.trim()),
      enabled: name.trim().length >= 3,
      staleTime: 30_000
    },
    queryClient
  );

export const useMigrationProposals = (enabled: boolean) =>
  useQuery(
    {
      queryKey: companyKeys.migration(),
      queryFn: CompanyMigrationAPI.proposals,
      enabled
    },
    queryClient
  );

export const usePlaybookPreview = (
  companyId: string | undefined,
  individualId: string | undefined
) =>
  useQuery(
    {
      queryKey: companyKeys.preview(companyId ?? "", individualId ?? ""),
      queryFn: () => PlaybookAPI.preview(companyId ?? "", individualId ?? ""),
      enabled: Boolean(companyId && individualId)
    },
    queryClient
  );

export const useCompanyBilling = (
  companyId: string | undefined,
  enabled = true
) =>
  useQuery(
    {
      queryKey: companyKeys.billing(companyId ?? ""),
      queryFn: () => BillingAPI.get(companyId ?? ""),
      enabled: Boolean(companyId) && enabled
    },
    queryClient
  );

/** Admins only; the backend answers `enabled: false` while time tracking is off. */
export const useNextExport = (companyId: string | undefined, enabled = true) =>
  useQuery(
    {
      queryKey: companyKeys.nextExport(companyId ?? ""),
      queryFn: () => BillingAPI.nextExport(companyId ?? ""),
      enabled: Boolean(companyId) && enabled
    },
    queryClient
  );

export const useContactLogins = (companyId: string | undefined) =>
  useQuery(
    {
      queryKey: companyKeys.logins(companyId ?? ""),
      queryFn: () => PortalFirmAPI.logins(companyId ?? ""),
      enabled: Boolean(companyId)
    },
    queryClient
  );

export const useCaseRequests = (companyId: string | undefined) =>
  useQuery(
    {
      queryKey: companyKeys.requests(companyId ?? ""),
      queryFn: () => PortalFirmAPI.requests(companyId ?? ""),
      enabled: Boolean(companyId)
    },
    queryClient
  );

export const useCaseApprovals = (
  companyId: string | undefined,
  individualId: string | undefined
) =>
  useQuery(
    {
      queryKey: companyKeys.approvals(companyId ?? "", individualId ?? ""),
      queryFn: () => PortalFirmAPI.approvals(companyId ?? "", individualId),
      enabled: Boolean(companyId && individualId)
    },
    queryClient
  );

export const useCaseMessages = (
  companyId: string | undefined,
  individualId: string | undefined
) =>
  useQuery(
    {
      queryKey: companyKeys.messages(companyId ?? "", individualId ?? ""),
      queryFn: () =>
        PortalFirmAPI.messages(companyId ?? "", individualId ?? ""),
      enabled: Boolean(companyId && individualId)
    },
    queryClient
  );
